import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useAtom } from 'jotai/index';
import { useSearchParams } from 'react-router-dom';
import styles from './css/index.module.scss';
import SimpleHeader from '../../ui-kit/SimpleHeader';
import Input from '../../ui-kit/Input';
import Button from '../../ui-kit/Button';
import useInputState from '../../hooks/useInputState';
import useTranslations from '../../hooks/useTranslations';
import { DICTIONARY } from '../../translations/dictionary';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../api';
import { profileAtom } from '../../atoms/accountAtoms';
import passwordRecovery from '../PasswordRecovery';
import { openInfoModalAtom } from '../../atoms/modalAtoms';
import PasswordServices from '../../services/PasswordServices';
import UnderlinedButton from '../../ui-kit/UnderlinedButton';
import useTutorials from '../../hooks/useTutorials';
import { isInitAtom } from '../../atoms/globalAtoms';

function PersonalData() {
  const { t } = useTranslations();
  const { saveStage } = useTutorials();
  const [profile, setProfile] = useAtom(profileAtom);
  const [isInit] = useAtom(isInitAtom);
  const [, openInfoModal] = useAtom(openInfoModalAtom);

  const [name, setName, nameError, setNameError] = useInputState('');
  const [email, setEmail, emailError, setEmailError] = useInputState('');
  const [phone, setPhone, phoneError, setPhoneError] = useInputState('');
  const [company, setCompany, companyError, setCompanyError] = useInputState('');
  const [position, setPosition, positionError, setPositionError] = useInputState('');
  const [newPassword, setNewPassword, newPasswordError, setNewPasswordError] = useInputState('');
  const [confirmPassword, setConfirmPassword, confirmPasswordError, setConfirmPasswordError] = useInputState('');

  const [searchParams, setSearchParams] = useSearchParams();
  const shouldAskToChangePassword = searchParams.get('ask_to_change_password') === 'true';

  const [loading, setLoading] = useState(false);

  const handleSubmit = (ev) => {
    ev.preventDefault();
    let hasError = false;

    if (!name) {
      setNameError(t(DICTIONARY.nameRequired));
      hasError = true;
    }
    if (!email) {
      setEmailError(t(DICTIONARY.emailRequired));
      hasError = true;
    }
    if (!company) {
      setCompanyError(t(DICTIONARY.companyRequired));
      hasError = true;
    }
    if (!position) {
      setPositionError(t(DICTIONARY.positionRequired));
      hasError = true;
    }

    if (newPassword || confirmPassword) {
      const passwordError = PasswordServices.hasValidationError(newPassword);
      if (passwordError) {
        setNewPasswordError(passwordError);
        hasError = true;
      }
      if (newPassword !== confirmPassword) {
        setConfirmPasswordError(t(DICTIONARY.passwordsNotMatch));
        hasError = true;
      }
    }

    if (!hasError) saveProfile().then();
  };

  const saveProfile = async () => {
    const payload = {
      name,
      company_name: company,
      company_position: position,
      phone,
    };

    if (newPassword) {
      payload.password = newPassword;
      payload.password_confirmation = confirmPassword;
    }

    const data = await sendRequest({
      request: Api.saveProfile,
      payload,
      warnErrorText: 'while saving profile',
      showErrorInModal: false,
    });

    if (data?.status === 'ok') {
      openInfoModal({ text: t(DICTIONARY.profileSaved) });
      setNewPassword('', true);
      setConfirmPassword('', true);
    } else if (data?.data) {
      handleErrors(data.data);
    } else {
      openInfoModal({ text: data?.message || t(DICTIONARY.error) });
    }
  };

  const handleErrors = (data) => {
    if (Array.isArray(data?.name)) setNameError(data.name.join('. '));
    if (Array.isArray(data?.email)) setEmailError(data.email.join('. '));
    if (Array.isArray(data?.phone)) setPhoneError(data.phone.join('. '));
    if (Array.isArray(data?.company_name)) setCompanyError(data.company_name.join('. '));
    if (Array.isArray(data?.company_position)) setPositionError(data.company_position.join('. '));

    if (Array.isArray(data?.password)) setNewPasswordError(data.password.join('. '));
    if (Array.isArray(data?.password_confirmation)) setConfirmPasswordError(data.password_confirmation.join('. '));
  };

  const getProfile = async () => {
    const data = await sendRequest({
      request: Api.getProfile,
      warnErrorText: 'while getting profile',
    });

    if (data?.status === 'ok') {
      setProfile(data.data.user);
    }
  };

  useEffect(() => {
    if (!profile) return;

    setName(profile.name);
    setEmail(profile.email);
    setPhone(profile.phone);
    setCompany(profile.company_name);
    setPosition(profile.company_position);
  }, [profile]);

  useEffect(() => {
    getProfile().then();
  }, []);

  const onReloadTutorialClick = async () => {
    await saveStage(1);
    window.location.href = '/configurator';
  };

  useEffect(() => {
    if (isInit && shouldAskToChangePassword) {
      setSearchParams({}, { replace: true });
      openInfoModal({
        text: t(DICTIONARY.askToEditPassword),
      });
    }
  }, [isInit]);

  return (
    <div className={styles.personal}>
      <div className={classNames('container')}>
        {/* <UnderlinedButton text="Перезагрузить обучение" onClick={onReloadTutorialClick} /> */}
        <div className={styles.personalInner}>
          <SimpleHeader
            subtitle={t(DICTIONARY.personalSubTitle)}
            title={t(DICTIONARY.personalTitle)}
            isCenter
          />

          <div className={styles.inputWrapper}>
            <Input
              text={t(DICTIONARY.fullName)}
              isRequired
              value={name}
              onChange={setName}
              width="100%"
              error={nameError}
              name="name"
            />
            <div className={styles.disabledInput}>
              <Input
                text="E-mail"
                isRequired
                value={email}
                onChange={setEmail}
                width="100%"
                error={emailError}
                name="email"
                disabled
              />
            </div>
            <Input
              text={t(DICTIONARY.phone)}
              value={phone}
              onChange={setPhone}
              width="100%"
              error={phoneError}
              name="phone"
              mask="+7 999 999 99 99"
            />
            <Input
              text={t(DICTIONARY.company)}
              isRequired
              value={company}
              onChange={setCompany}
              width="100%"
              error={companyError}
              name="company"
            />
            <Input
              text={t(DICTIONARY.position)}
              isRequired
              value={position}
              onChange={setPosition}
              width="100%"
              error={positionError}
              name="position"
            />
            <Input
              text={t(DICTIONARY.newPassword)}
              value={newPassword}
              onChange={setNewPassword}
              width="100%"
              error={newPasswordError}
              type="password"
            />
            <Input
              text={t(DICTIONARY.confirmNewPassword)}
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder=""
              width="100%"
              error={confirmPasswordError}
              type="password"
            />

            <div className={styles.buttonWrapper}>
              <Button
                text={t(DICTIONARY.saveChanges)}
                onClick={handleSubmit}
                width="100%"
                isDisabled={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PersonalData;
